import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, Sparkles, ChevronLeft } from "lucide-react";
import { api, API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

const emptyItem = () => ({
  description: "",
  hsn: "",
  quantity: 1,
  unit: "Pcs",
  unit_price: 0,
  gst_rate: 18,
});

const emptyParty = () => ({
  name: "",
  address: "",
  city: "",
  state: "",
  state_code: "",
  gstin: "",
  pan: "",
});

export default function ExtractReview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("extracted_po");
    if (!raw) {
      toast.error("No extracted PO found. Please upload again.");
      navigate("/");
      return;
    }
    const d = JSON.parse(raw);
    setData({
      po_number: d.po_number || "",
      po_date: d.po_date || "",
      invoice_number: "",
      buyer: { ...emptyParty(), ...(d.buyer || {}) },
      ship_to: { ...emptyParty(), ...(d.ship_to || d.buyer || {}) },
      seller: { ...emptyParty(), ...(d.seller || {}) },
      line_items: (d.line_items || []).map((i) => ({ ...emptyItem(), ...i })),
      po_file_id: d.po_file_id || null,
      po_total: Number(d.po_total) || 0,
      place_of_supply: d.buyer?.state || "",
      notes: d.notes || "",
    });
  }, [navigate]);

  if (!data) return null;

  const updateField = (path, value) => {
    setData((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const updateItem = (idx, key, value) => {
    setData((prev) => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], [key]: value };
      return { ...prev, line_items: items, itemsEdited: true };
    });
  };

  const addItem = () =>
    setData((p) => ({ ...p, line_items: [...p.line_items, emptyItem()], itemsEdited: true }));

  const removeItem = (idx) =>
    setData((p) => ({
      ...p,
      line_items: p.line_items.filter((_, i) => i !== idx),
      itemsEdited: true,
    }));

  const generate = async () => {
    setSaving(true);
    try {
      const { itemsEdited, ...rest } = data;
      const payload = {
        ...rest,
        po_total: itemsEdited ? 0 : Number(data.po_total) || 0,
        invoice_number: (data.invoice_number || "").trim() || null,
        line_items: data.line_items.map((i) => ({
          ...i,
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
          gst_rate: Number(i.gst_rate) || 0,
        })),
      };
      const { data: inv } = await api.post("/invoice/generate", payload);
      toast.success(`Invoice ${inv.invoice_number} generated`);
      sessionStorage.removeItem("extracted_po");
      navigate(`/invoice/${inv.id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to generate invoice");
    } finally {
      setSaving(false);
    }
  };

  const subtotal = data.line_items.reduce(
    (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
    0,
  );

  return (
    <div className="grid grid-cols-2 h-screen">
      {/* Left: PO Preview */}
      <div className="border-r border-zinc-200 bg-zinc-100 flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-200 bg-white flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500">
              Source Document
            </div>
            <div className="text-sm font-medium">Purchase Order PDF</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            data-testid="back-to-dashboard"
          >
            <ChevronLeft size={14} className="mr-1" />
            Back
          </Button>
        </div>
        <div className="flex-1 overflow-hidden">
          {data.po_file_id ? (
            <iframe
              src={`${API}/po/${data.po_file_id}/file`}
              className="w-full h-full"
              title="PO Preview"
              data-testid="po-preview-iframe"
            />
          ) : (
            <div className="p-6 text-zinc-500">PO file not available</div>
          )}
        </div>
      </div>

      {/* Right: Editable form */}
      <div className="overflow-y-auto flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-200 bg-white sticky top-0 z-10 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-blue-600 flex items-center gap-1">
              <Sparkles size={12} /> Extracted by Gemini 3 Flash
            </div>
            <div className="text-lg font-semibold">Review & Generate Invoice</div>
          </div>
        </div>

        <div className="p-6 space-y-6 pb-32">
          {/* PO meta */}
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
              PO Details
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field
                label="PO Number"
                value={data.po_number}
                onChange={(v) => updateField("po_number", v)}
                testid="field-po-number"
              />
              <Field
                label="PO Date"
                value={data.po_date}
                onChange={(v) => updateField("po_date", v)}
                testid="field-po-date"
              />
              <Field
                label="PO Net Amount (₹)"
                value={data.po_total}
                onChange={(v) => updateField("po_total", v)}
                testid="field-po-total"
              />
            </div>
            <div className="mt-3">
              <Field
                label="Invoice Number (optional — leave blank for auto INV-<PO>)"
                value={data.invoice_number}
                onChange={(v) => updateField("invoice_number", v)}
                testid="field-invoice-number"
              />
            </div>
            <div className="text-[10px] text-zinc-500 mt-2">
              The invoice Grand Total will be reconciled to this PO Net Amount by adjusting the last line item.
            </div>
          </Card>

          {/* Buyer */}
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
              Bill To (Buyer)
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" value={data.buyer.name} onChange={(v) => updateField("buyer.name", v)} testid="field-buyer-name" />
              <Field label="GSTIN" value={data.buyer.gstin} onChange={(v) => updateField("buyer.gstin", v)} testid="field-buyer-gstin" />
              <div className="col-span-2">
                <Field label="Address" value={data.buyer.address} onChange={(v) => updateField("buyer.address", v)} testid="field-buyer-address" />
              </div>
              <Field label="City" value={data.buyer.city} onChange={(v) => updateField("buyer.city", v)} testid="field-buyer-city" />
              <Field label="State" value={data.buyer.state} onChange={(v) => updateField("buyer.state", v)} testid="field-buyer-state" />
              <Field label="State Code" value={data.buyer.state_code} onChange={(v) => updateField("buyer.state_code", v)} testid="field-buyer-state-code" />
              <Field label="PAN" value={data.buyer.pan} onChange={(v) => updateField("buyer.pan", v)} testid="field-buyer-pan" />
            </div>
          </Card>

          {/* Ship To */}
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
              Ship To
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" value={data.ship_to.name} onChange={(v) => updateField("ship_to.name", v)} testid="field-ship-name" />
              <Field label="GSTIN" value={data.ship_to.gstin} onChange={(v) => updateField("ship_to.gstin", v)} testid="field-ship-gstin" />
              <div className="col-span-2">
                <Field label="Address" value={data.ship_to.address} onChange={(v) => updateField("ship_to.address", v)} testid="field-ship-address" />
              </div>
              <Field label="State" value={data.ship_to.state} onChange={(v) => updateField("ship_to.state", v)} testid="field-ship-state" />
              <Field label="State Code" value={data.ship_to.state_code} onChange={(v) => updateField("ship_to.state_code", v)} testid="field-ship-state-code" />
            </div>
          </Card>

          {/* Line items */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider text-zinc-500">
                Line Items
              </div>
              <Button variant="outline" size="sm" onClick={addItem} data-testid="add-line-item">
                <Plus size={14} className="mr-1" /> Add Row
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[900px]">
                <thead className="text-zinc-500">
                  <tr className="text-left">
                    <th className="py-2 pr-2 font-medium min-w-[220px]">Description</th>
                    <th className="py-2 pr-2 font-medium w-32">HSN</th>
                    <th className="py-2 pr-2 font-medium w-24">Qty</th>
                    <th className="py-2 pr-2 font-medium w-32">Rate ₹</th>
                    <th className="py-2 pr-2 font-medium w-24">GST %</th>
                    <th className="py-2 pr-2 font-medium w-32 text-right">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.line_items.map((it, i) => (
                    <tr key={i} className="border-t border-zinc-100" data-testid={`line-item-row-${i}`}>
                      <td className="py-1 pr-2">
                        <Input
                          value={it.description}
                          onChange={(e) => updateItem(i, "description", e.target.value)}
                          className="h-8 text-xs"
                          data-testid={`item-desc-${i}`}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Input value={it.hsn} onChange={(e) => updateItem(i, "hsn", e.target.value)} className="h-8 text-xs mono" data-testid={`item-hsn-${i}`} />
                      </td>
                      <td className="py-1 pr-2">
                        <Input type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="h-8 text-xs mono text-right" data-testid={`item-qty-${i}`} />
                      </td>
                      <td className="py-1 pr-2">
                        <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="h-8 text-xs mono text-right" data-testid={`item-rate-${i}`} />
                      </td>
                      <td className="py-1 pr-2">
                        <Input type="number" value={it.gst_rate} onChange={(e) => updateItem(i, "gst_rate", e.target.value)} className="h-8 text-xs mono text-right" data-testid={`item-gst-${i}`} />
                      </td>
                      <td className="py-1 pr-2 mono text-right tabular">
                        {((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-zinc-400 hover:text-red-600"
                          onClick={() => removeItem(i)}
                          data-testid={`item-remove-${i}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-zinc-200">
                    <td colSpan="5" className="pt-3 pr-2 text-right text-zinc-500 uppercase text-xs tracking-wider">
                      Subtotal (Taxable)
                    </td>
                    <td className="pt-3 pr-2 text-right mono font-semibold tabular" data-testid="subtotal-value">
                      ₹ {subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        {/* Sticky footer action */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-zinc-200 px-6 py-3 flex items-center justify-between">
          <div className="text-xs text-zinc-500">
            Auto invoice # will be assigned on generate
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/")} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={generate}
              disabled={saving || data.line_items.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="generate-invoice-btn"
            >
              {saving ? "Generating…" : "Generate GST Invoice →"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-xs"
        data-testid={testid}
      />
    </div>
  );
}
